/**
 * ReportViewer
 * 
 * Displays reports with text sections, charts, tables, and metrics
 */

import React from 'react';
import type { ReportArtifact, ReportSection, ChartContent, MetricContent, TableContent } from '../../artifacts';
import { useWorkspace } from '../../artifacts';

interface ReportViewerProps {
  artifact: ReportArtifact;
}

// Simple bar chart component
const SimpleBarChart: React.FC<{ data: ChartContent['data'] }> = ({ data }) => {
  const maxValue = Math.max(...data.map((d) => d.value));
  
  return (
    <div className="space-y-2">
      {data.map((item, index) => (
        <div key={index} className="flex items-center gap-3">
          <span className="w-24 text-xs text-neutral-600 truncate">{item.label}</span>
          <div className="flex-1 h-6 bg-neutral-100 rounded overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded transition-all duration-500"
              style={{ width: `${(item.value / maxValue) * 100}%`, backgroundColor: item.color }}
            />
          </div>
          <span className="w-16 text-xs font-medium text-neutral-700 text-right">
            {item.value.toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
};

// Simple pie chart (placeholder - in production use a proper charting library)
const SimplePieChart: React.FC<{ data: ChartContent['data'] }> = ({ data }) => {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
  
  return (
    <div className="flex items-center gap-6">
      <div className="w-32 h-32 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
        <span className="text-white font-semibold">{total.toLocaleString()}</span>
      </div>
      <div className="space-y-1">
        {data.map((item, index) => (
          <div key={index} className="flex items-center gap-2 text-xs">
            <div
              className="w-3 h-3 rounded"
              style={{ backgroundColor: item.color || colors[index % colors.length] }}
            />
            <span className="text-neutral-600">{item.label}</span>
            <span className="font-medium text-neutral-800">
              ({((item.value / total) * 100).toFixed(1)}%)
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Metric card
const MetricCard: React.FC<{ content: MetricContent }> = ({ content }) => {
  const changeColor = content.changeDirection === 'up' 
    ? 'text-green-600' 
    : content.changeDirection === 'down' 
      ? 'text-red-600' 
      : 'text-neutral-500';

  return (
    <div className="p-4 bg-gradient-to-br from-neutral-50 to-white rounded-xl border border-neutral-200">
      <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">{content.label}</p>
      <p className="text-2xl font-semibold text-neutral-900">
        {content.format === 'currency' && '$'}
        {typeof content.value === 'number' ? content.value.toLocaleString() : content.value}
        {content.format === 'percent' && '%'}
      </p>
      {content.change !== undefined && (
        <p className={`text-xs mt-1 ${changeColor}`}>
          {content.changeDirection === 'up' ? '↑' : content.changeDirection === 'down' ? '↓' : '→'}
          {' '}{Math.abs(content.change).toFixed(1)}%
        </p>
      )}
    </div>
  );
};

// Table view
const TableView: React.FC<{ content: TableContent }> = ({ content }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-neutral-50">
          {content.columns.map((col) => (
            <th key={col.key} className="px-4 py-2 text-left font-medium text-neutral-600 border-b border-neutral-200">
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {content.rows.map((row, idx) => (
          <tr key={idx} className="hover:bg-neutral-50">
            {content.columns.map((col) => (
              <td key={col.key} className="px-4 py-2 text-neutral-800 border-b border-neutral-100">
                {String(row[col.key] ?? '')}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// Section renderer
const SectionRenderer: React.FC<{ section: ReportSection }> = ({ section }) => {
  return (
    <div className="mb-6">
      {section.title && (
        <h3 className="text-sm font-medium text-neutral-700 mb-3">{section.title}</h3>
      )}
      
      {section.type === 'text' && (
        <div className="prose prose-sm prose-neutral max-w-none">
          <p className="text-neutral-600 whitespace-pre-wrap">
            {(section.content as { markdown: string }).markdown}
          </p>
        </div>
      )}

      {section.type === 'chart' && (
        <div className="p-4 bg-white rounded-lg border border-neutral-200">
          {(section.content as ChartContent).chartType === 'bar' || (section.content as ChartContent).chartType === 'line' ? (
            <SimpleBarChart data={(section.content as ChartContent).data} />
          ) : (
            <SimplePieChart data={(section.content as ChartContent).data} />
          )}
        </div>
      )}

      {section.type === 'metric' && (
        <MetricCard content={section.content as MetricContent} />
      )}

      {section.type === 'table' && (
        <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
          <TableView content={section.content as TableContent} />
        </div>
      )}
    </div>
  );
};

export const ReportViewer: React.FC<ReportViewerProps> = ({ artifact }) => {
  const { updateArtifact } = useWorkspace();

  const isEmpty = artifact.sections.length === 0;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 bg-white">
        <div>
          <input
            type="text"
            value={artifact.title}
            onChange={(e) => updateArtifact(artifact.id, { title: e.target.value })}
            className="text-lg font-semibold text-neutral-900 bg-transparent border-none outline-none focus:ring-0"
            placeholder="Report Title"
          />
          <p className="text-xs text-neutral-500 mt-0.5">
            Created {new Date(artifact.createdAt).toLocaleDateString()} · {artifact.sections.length} section{artifact.sections.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="px-3 py-1.5 text-xs font-medium rounded bg-neutral-100 text-neutral-700 hover:bg-neutral-200 transition-colors"
          >
            Export PDF
          </button>
          <button
            type="button"
            className="px-3 py-1.5 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            Share
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto bg-neutral-50">
        <div className="max-w-3xl mx-auto px-6 py-8">
          {isEmpty ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-neutral-100 flex items-center justify-center">
                <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-neutral-700 mb-2">Empty Report</h3>
              <p className="text-sm text-neutral-500 max-w-sm mx-auto">
                Ask the assistant to generate report sections with data, charts, and insights.
              </p>
            </div>
          ) : (
            artifact.sections.map((section) => (
              <SectionRenderer key={section.id} section={section} />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportViewer;

